"use client";

import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import DataTable from "../../../../../../Common/DataTable/DataTable";
import {
  DELETE_PROCUREMENT_REQUEST,
  GET_ALL_DEPARTMENT_LIST_REQUEST,
  GET_ALL_PROCUREMENT_LIST_REQUEST,
  PUT_PROCUREMENT_REQUEST,
  GET_ALL_DEPARTMENT_CATEGORY_LIST_REQUEST,
} from "../../../../../../Utils/Constant";
import DataTableFooter from "../../../../../../Common/DataTableFooter/DataTableFooter";
import { ItemsCategoryColumns } from "./ItemsCategoryColumns";
import { useNavigate } from "react-router-dom";
import ConfirmationATM from "../../../../../../Common/ConfirmationATM";
import DataTableHeader from "../../../../../../Common/DataTableHeader/DataTableHeader";
import DocumentPreviewModal from "./DocumentPreviewModal";
import DocumentEditModal from "./DocumentEditModal";
import {
  hasDeleteAccess,
  hasEditAccess,
} from "../../../../../../Common/CheckAccess";

const searchOptions = [
  { label: "Status", value: "status" },
  { label: "Request Type", value: "request_type" },
  { label: "Department", value: "department_id" },
  { label: "Category", value: "category_id" },
];

const RestaurantCategoryTable = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const navigate = useNavigate();

  // State management
  const [categoryData, setCategoryData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [gloablFilterType, setGloablFilterType] = useState("name");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [selectedItemForUpload, setSelectedItemForUpload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});

  // Document preview states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

  // Document edit states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocuments, setEditingDocuments] = useState([]);
  const [selectedEditItem, setSelectedEditItem] = useState(null);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [newFilesToAdd, setNewFilesToAdd] = useState([]);
  const [searchBy, setSearchBy] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  
  const {
    procurementList,
    loading,
    addProcurement,
    updateProcurement,
    deleteProcurement,
  } = useSelector((state) => state.procurement);

  const departmentList = useSelector(state => state.department.departmentList);
  const departmentCategoryList = useSelector(state => state.department.departmentCategoryList);
  const [permissions, setPermissions] = useState([]);

  const existingPermissions = localStorage.getItem("checkPermission");
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);
  
  useEffect(() => {
    dispatch({ type: GET_ALL_DEPARTMENT_LIST_REQUEST });
    dispatch({ type: GET_ALL_DEPARTMENT_CATEGORY_LIST_REQUEST, payload: {
      body: {
        isAll: true
      }
    }});
  }, [dispatch]);

  useEffect(() => {
    if (departmentList?.data?.departments) {
      setDepartmentOptions(
        departmentList.data.departments.map(dep => ({
          label: dep.name,
          value: dep.id,
        }))
      );
    }
  }, [departmentList]);

  useEffect(() => {
    if (departmentCategoryList?.data && departmentCategoryList?.data?.length) {
      setCategoryOptions(
        departmentCategoryList?.data?.map(cat => ({
          label: cat.name,
          value: cat.id,
        }))
      );
    }
  }, [departmentCategoryList]);
  
  // Fetch procurement data
  useEffect(() => {
    const payload = {
      type: "procurement",
      page,
      limit: pageSize,
      sortBy: sortBy ? `${sortBy},${sortOrder}` : undefined,
    };
  
    if (searchBy && searchValue) {
      payload[searchBy] = searchValue;
    }
    dispatch({
      type: GET_ALL_PROCUREMENT_LIST_REQUEST,
      payload
    });
  }, [
    dispatch,
    page,
    pageSize,
    sortBy,
    sortOrder,
    addProcurement,
    updateProcurement,
    deleteProcurement,
    globalFilter,
  ]);

  // Handle approve/reject actions
  const handleApproveReject = (status, data) => {
    dispatch({
      type: PUT_PROCUREMENT_REQUEST,
      payload: {
        requestId: data.id,
        type: "status-update",
        body: { status: status },
        data: data,
      },
    });
  };

  // Handle delete functionality
  const handleDelete = (selectData) => {
    setSelectedUser(selectData);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    dispatch({
      type: DELETE_PROCUREMENT_REQUEST,
      payload: { id: selectedUser?.id },
    });
    setShowDeleteModal(false);
  };

  // File upload functionality for new uploads
  const handleFileUpload = (data) => {
    setSelectedItemForUpload(data);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !selectedItemForUpload) return;

    await processFileUpload(files, selectedItemForUpload.id, "initial_upload");

    // Reset file input and selected item
    event.target.value = "";
    setSelectedItemForUpload(null);
  };

  // File upload functionality for editing (adding more files)
  const handleEditFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !selectedEditItem) return;

    // Add new files to the editing state
    setNewFilesToAdd((prev) => [...prev, ...files]);

    // Reset file input
    event.target.value = "";
  };

  const processFileUpload = async (
    files,
    procurementId,
    uploadType = "initial_upload"
  ) => {
    // Validate file types and size
    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ];
    const allowedExtensions = [".pdf", ".png", ".jpeg", ".jpg"];
    const maxFileSize = 10 * 1024 * 1024; // 10MB

    const validFiles = files.filter((file) => {
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      const isValidSize = file.size <= maxFileSize;

      if (!isValidSize) {
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return false;
      }

      return isValidType || isValidExtension;
    });

    if (validFiles.length !== files.length) {
      alert(
        "Some files were skipped. Only PDF, PNG, JPEG, and JPG files under 10MB are allowed."
      );
    }

    if (validFiles.length === 0) {
      alert(
        "No valid files selected. Please choose PDF, PNG, JPEG, or JPG files under 10MB."
      );
      return;
    }

    // Add to uploading set
    setUploadingFiles((prev) => new Set(prev).add(procurementId));
    setUploadProgress((prev) => ({ ...prev, [procurementId]: 0 }));

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const currentProgress = prev[procurementId] || 0;
          if (currentProgress >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return {
            ...prev,
            [procurementId]: currentProgress + 10,
          };
        });
      }, 200);

      // Create FormData with all files together
      const formData = new FormData();

      // Add all files to FormData
      validFiles.forEach((file, index) => {
        formData.append(`invoice_files`, file); // Backend expects 'invoice_files' array
      });

      formData.append("requestId", procurementId);
      formData.append("uploadType", uploadType);
      formData.append("fileCount", validFiles.length.toString());

      // Add file metadata
      const fileMetadata = validFiles.map((file) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }));
      formData.append("fileMetadata", JSON.stringify(fileMetadata));

      const response = await dispatch({
        type: PUT_PROCUREMENT_REQUEST,
        payload: {
          requestId: procurementId,
          type: "upload",
          body: formData,
        },
      });

      // Complete progress
      setUploadProgress((prev) => ({
        ...prev,
        [procurementId]: 100,
      }));

      // Refresh data
      dispatch({
        type: GET_ALL_PROCUREMENT_LIST_REQUEST,
        payload: { type: "procurement" },
      });

      // Show success message
      setTimeout(() => {
        // alert(`${validFiles.length} file(s) uploaded successfully!`)
      }, 500);

      return response;
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload some files. Please try again.");
      throw error;
    } finally {
      // Cleanup
      setTimeout(() => {
        setUploadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(procurementId);
          return newSet;
        });
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[procurementId];
          return newProgress;
        });
      }, 1000);
    }
  };

  // Document preview functionality
  const handleDocumentPreview = (data) => {
    if (data.invoice_files && data.invoice_files.length > 0) {
      const documents = data.invoice_files.map((file, index) => ({
        id: file.id || index,
        url: file.path || file.url || file, // Backend uses 'path' instead of 'url'
        fileName:
          file.filename ||
          file.fileName ||
          file.path?.split("/").pop() ||
          `Document ${index + 1}`, // Backend uses 'filename'
        fileType:
          file.mimetype ||
          file.fileType ||
          getFileTypeFromUrl(file.path || file.url || file), // Backend uses 'mimetype'
        procurementId: data.id,
        uploadDate: file.uploaded_at || file.uploadDate || file.created_at, // Backend uses 'uploaded_at'
        fileSize: file.size || file.fileSize, // Backend uses 'size'
      }));
      setSelectedDocuments(documents);
      setCurrentDocumentIndex(0);
      setShowDocumentModal(true);
    }
  };

  // Document edit functionality
  const handleEditDocuments = (data) => {
    setSelectedEditItem(data);
    // setEditingDocuments(data.invoice_files || [])
    // Map backend structure to frontend structure
    const mappedDocuments = (data.invoice_files || []).map((file, index) => ({
      id: file.id || index,
      url: file.path || file.url || file,
      fileName:
        file.filename ||
        file.fileName ||
        file.path?.split("/").pop() ||
        `Document ${index + 1}`,
      fileType:
        file.mimetype ||
        file.fileType ||
        getFileTypeFromUrl(file.path || file.url || file),
      uploadDate: file.uploaded_at || file.uploadDate || file.created_at,
      fileSize: file.size || file.fileSize,
      originalFileName:
        file.filename ||
        file.fileName ||
        file.path?.split("/").pop() ||
        `Document ${index + 1}`,
    }));
    setEditingDocuments(mappedDocuments);
    setDocumentsToDelete([]);
    setNewFilesToAdd([]);
    setShowEditModal(true);
  };

  const handleAddMoreDocuments = () => {
    if (selectedEditItem) {
      editFileInputRef.current?.click();
    }
  };

  const handleMarkDocumentForDeletion = (documentId) => {
    setDocumentsToDelete((prev) => {
      if (prev.includes(documentId)) {
        return prev.filter((id) => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleRemoveNewFile = (fileIndex) => {
    setNewFilesToAdd((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleSaveDocumentChanges = async (updatedDocuments) => {
    try {
      setUploadingFiles((prev) => new Set(prev).add(selectedEditItem.id));

      // Create FormData for the complete update
      const formData = new FormData();

      // Add existing documents that are not marked for deletion
      const existingDocuments = updatedDocuments.filter(
        (doc) => !documentsToDelete.includes(doc.id)
      );

      // Add new files to FormData
      newFilesToAdd.forEach((file) => {
        formData.append(`invoice_files`, file);
      });

      // Add metadata for the update
      formData.append("requestId", selectedEditItem.id);
      formData.append("updateType", "edit_documents");
      formData.append("documentsToDelete", JSON.stringify(documentsToDelete));
      formData.append("existingDocuments", JSON.stringify(existingDocuments));
      formData.append("newFileCount", newFilesToAdd.length.toString());

      // Add new file metadata
      if (newFilesToAdd.length > 0) {
        const newFileMetadata = newFilesToAdd.map((file) => ({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        }));
        formData.append("newFileMetadata", JSON.stringify(newFileMetadata));
      }

      await dispatch({
        type: PUT_PROCUREMENT_REQUEST,
        payload: {
          requestId: selectedEditItem.id,
          type: "update_documents",
          body: formData,
        },
      });

      // Refresh data
      dispatch({
        type: GET_ALL_PROCUREMENT_LIST_REQUEST,
        payload: { type: "procurement" },
      });

      // Close modal and reset states
      setShowEditModal(false);
      setEditingDocuments([]);
      setSelectedEditItem(null);
      setDocumentsToDelete([]);
      setNewFilesToAdd([]);

      // alert("Documents updated successfully!")
    } catch (error) {
      console.error("Error updating documents:", error);
      alert("Failed to update documents. Please try again.");
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedEditItem?.id);
        return newSet;
      });
    }
  };

  // Delete document from preview modal
  const handleDeleteDocument = (procurementId, documentIndex) => {
    // if (confirm("Are you sure you want to delete this document?")) {
    const documentToDelete = selectedDocuments[documentIndex];

    // Create FormData for deletion
    const formData = new FormData();
    formData.append("requestId", procurementId);
    formData.append("documentId", documentToDelete.id || documentIndex);
    formData.append("updateType", "delete_single_document");

    dispatch({
      type: PUT_PROCUREMENT_REQUEST,
      payload: {
        requestId: procurementId,
        type: "delete_document",
        body: formData,
      },
    });

    // Update local state
    if (selectedDocuments.length <= 1) {
      setShowDocumentModal(false);
      setSelectedDocuments([]);
      setCurrentDocumentIndex(0);
    } else {
      const updatedDocs = [...selectedDocuments];
      updatedDocs.splice(documentIndex, 1);
      setSelectedDocuments(updatedDocs);
      if (currentDocumentIndex >= updatedDocs.length) {
        setCurrentDocumentIndex(updatedDocs.length - 1);
      }
    }

    // Refresh data
    dispatch({
      type: GET_ALL_PROCUREMENT_LIST_REQUEST,
      payload: { type: "procurement" },
    });
    // }
  };

  const getFileTypeFromUrl = (url) => {
    const extension = url.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return "application/pdf";
      case "png":
        return "image/png";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      default:
        return "unknown";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Transform data for table display
  useEffect(() => {
    if (procurementList?.data?.length) {
      const transformed = procurementList?.data?.map((data) => ({
        id: data.id,
        department_name: data?.department?.name || "N/A",
        category_name: data?.category?.name || "N/A",
        requested_by: data?.staff
          ? `${data?.staff?.first_name} ${data?.staff?.last_name}`
          : "-",
        status: data.status || "N/A",
        created_at: data.created_at?.split("T")[0] || "N/A",
        request_type: (
          <div className="d-flex flex-wrap gap-2">
          <span className={`badge ${getStatusBadgeClass(data?.request_type ? "external" : "internal")}`}>
            {data?.request_type ? "External" : "Internal" || "N/A"}
          </span>
          </div>
        ),
        invoice_file:
          data?.invoice_files && data.invoice_files.length > 0 ? (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                  onClick={() => handleDocumentPreview(data)}
                  title="Preview Invoice"
                >
                  <i className="bi bi-eye"></i>
                  <span>Preview ({data.invoice_files.length})</span>
                </button>
                {data?.status === "pending" &&
                  hasEditAccess(permissions, "inventory") && (
                    <button
                      className="btn btn-outline-warning btn-sm d-flex align-items-center gap-2"
                      onClick={() => handleEditDocuments(data)}
                      title="Edit Invoice"
                    >
                      <i className="bi bi-pencil-square"></i>
                      <span>Edit</span>
                    </button>
                  )}
              </div>
              {/* <small className="text-muted">
                {data.invoice_files.length} file{data.invoice_files.length > 1 ? "s" : ""}
              </small> */}
            </div>
          ) : (
            <span className="text-muted">No Invoice</span>
          ),
        items: (
          <button
            className="btn btn-link p-0"
            onClick={() => {
              navigate(`/procurements/${data?.id}`, { state: data });
            }}
          >
            <i className="bi bi-eye me-1"></i>
            View
          </button>
        ),
        request_actions: (
          <div className="d-flex flex-wrap gap-2">
            {(data?.invoice_files?.length > 0 ||
              (!data?.request_type &&
                data?.inventoryAvailability?.every(
                  (item) => item.available
                ))) &&
            data?.status === "pending" ? (
              <>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => handleApproveReject("approved", data)}
                  title="Approve Request"
                  disabled={!["admin", "super_admin", "restaurant_manager"].includes(role)}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Approve
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleApproveReject("rejected", data)}
                  title="Reject Request"
                  // disabled={!hasEditAccess(permissions, "inventory")}
                  disabled={!["admin", "super_admin", "restaurant_manager"].includes(role)}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Reject
                </button>
              </>
            ) : !data?.request_type &&
              !data?.inventoryAvailability?.every((item) => item.available) &&
              data?.status === "pending" ? (
              <div className="d-flex flex-column gap-1">
                <span className={`badge bg-danger`}>Insufficient Stock</span>
              </div>
            ) : data?.status === "pending" ? (
              <div className="d-flex flex-column gap-1">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleFileUpload(data)}
                  disabled={
                    uploadingFiles.has(data.id) ||
                    !["admin", "super_admin", "restaurant_manager"].includes(role)
                  }
                >
                  {uploadingFiles.has(data.id) ? (
                    <>
                      <i className="bi bi-hourglass-split me-1"></i>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Upload Invoice
                    </>
                  )}
                </button>
                {uploadingFiles.has(data.id) && (
                  <div className="progress" style={{ height: "4px" }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: `${uploadProgress[data.id] || 0}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ) : (
              <span className={`badge ${getStatusBadgeClass(data.status)}`}>
                {data?.status === "approved" ? "Completed" : "Rejected"}
              </span>
            )}
          </div>
        ),
        actions: (
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() =>
                navigate(`/procurement-edit/${data.id}`, { state: data })
              }
              title="Edit Request"
              disabled={
                data?.status === "approved" ||
                data?.status === "rejected" ||
                !hasEditAccess(permissions, "inventory")
              }
            >
              <i className="bi bi-pencil-square me-1"></i>
              Edit
            </button>
            {hasDeleteAccess(permissions, "inventory") && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleDelete(data)}
                title="Delete Request"
                disabled={data?.status === "approved"}
              >
                <i className="bi bi-trash me-1"></i>
                Delete
              </button>
            )}
          </div>
        ),
      }));

      setCategoryData(transformed);
    } else {
      setCategoryData([]);
    }
  }, [procurementList, uploadingFiles, uploadProgress]);

  // Table functionality
  const handleSortChange = (sort) => {
    setSortBy(sort.id);
    setSortOrder(sort.desc ? "desc" : "asc");
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Extract pagination info
  const totalEntries = procurementList?.pagination?.totalResults || 0;
  const totalPages = procurementList?.pagination?.totalPages || 1;
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-success";
      case "rejected":
        return "bg-danger";
      case "pending":
        return "bg-warning text-dark";
      case "internal":
        return "bg-primary";
      default:
        return "bg-secondary";
    }
  };

  return (
    <div>
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        onChange={handleFileSelect}
      />

      <input
        type="file"
        ref={editFileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png"
        multiple
        onChange={handleEditFileSelect}
      />
<div className="d-flex flex-wrap align-items-end gap-4 mb-3">
<div className="flex-grow-1 flex-md-shrink-0" style={{ flexBasis: "40%" }}>

      <DataTableHeader
        pageSize={pageSize}
        setPageSize={setPageSize}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        setCurrentPage={setPage}
        gloablFilterType={gloablFilterType}
        setGloablFilterType={setGloablFilterType}
        flage="category"
      />
      </div>
      <div className="d-flex gap-3 align-items-end mb-3">
        <div>
          <label className="form-label">Search By</label>
          <select
            className="form-select"
            value={searchBy}
            onChange={(e) => {
              setSearchBy(e.target.value);
              setSearchValue("");
            }}
          >
            <option value="">Select Filter</option>
            {searchOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {searchBy && (
          <div>
            <label className="form-label">Value</label>
            <select
              className="form-select"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            >
              <option value="">Select Value</option>
              {/* Dynamic options based on searchBy */}
              {searchBy === "status" && [
                <option key="approved" value="approved">Approved</option>,
                <option key="rejected" value="rejected">Rejected</option>,
                <option key="pending" value="pending">Pending</option>,
              ]}
              {searchBy === "request_type" && [
                <option key="internal" value="true">Internal</option>,
                <option key="external" value="false">External</option>,
              ]}
              {searchBy === "department_id" && departmentOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
              {searchBy === "category_id" && categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        <button
          className="btn btn-primary mt-4"
          onClick={() => {
            setPage(1); 
            const payload = {
              type: "procurement",
              page: 1,
              limit: pageSize,
              sortBy: sortBy ? `${sortBy},${sortOrder}` : undefined,
            };
        
            if (searchBy && searchValue) {
              payload[searchBy] = searchValue;
            }
        
            dispatch({
              type: GET_ALL_PROCUREMENT_LIST_REQUEST,
              payload,
            });
          }}
          disabled={!searchBy || !searchValue}
        >
          Apply
        </button>

        <button
          className="btn btn-outline-secondary mt-4"
          onClick={() => {
            setSearchBy("");
            setSearchValue("");
            setPage(1);
            dispatch({
              type: GET_ALL_PROCUREMENT_LIST_REQUEST,
              payload: {
                type: "procurement",
                page: 1,
                limit: pageSize,
              },
            });
          }}
        >
          Reset
        </button>
      </div>
    </div>
      <DataTable
        columns={ItemsCategoryColumns}
        data={categoryData}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onSortChange={handleSortChange}
        isLoading={loading}
      />

      <DataTableFooter
        currentPage={page}
        totalPages={totalPages}
        setCurrentPage={handlePageChange}
        totalEntries={totalEntries}
        pageSize={pageSize}
      />

      <ConfirmationATM
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDelete}
        message={`Are you sure you want to delete request for ${selectedUser?.department?.name} items?`}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        showModal={showDocumentModal}
        documents={selectedDocuments}
        currentIndex={currentDocumentIndex}
        onClose={() => {
          setShowDocumentModal(false);
          setSelectedDocuments([]);
          setCurrentDocumentIndex(0);
        }}
        onNavigate={setCurrentDocumentIndex}
        onDeleteDocument={handleDeleteDocument}
        formatFileSize={formatFileSize}
        permissions={permissions}
      />

      {/* Document Edit Modal */}
      <DocumentEditModal
        showModal={showEditModal}
        documents={editingDocuments}
        newFiles={newFilesToAdd}
        documentsToDelete={documentsToDelete}
        onClose={() => {
          setShowEditModal(false);
          setEditingDocuments([]);
          setSelectedEditItem(null);
          setDocumentsToDelete([]);
          setNewFilesToAdd([]);
        }}
        onSave={handleSaveDocumentChanges}
        onAddMore={handleAddMoreDocuments}
        onMarkForDeletion={handleMarkDocumentForDeletion}
        onRemoveNewFile={handleRemoveNewFile}
        formatFileSize={formatFileSize}
        isUploading={uploadingFiles.has(selectedEditItem?.id)}
      />
    </div>
  );
};

export default RestaurantCategoryTable;
